import { FC } from 'react';
import {
  WellsOverviewSection as SharedWellsOverviewSection,
  type WellsOverviewConfig,
} from 'pages/documents/structuring/wells-overview-section';
import type { StagedWell } from './types-well';
import {
  fetchWellOverviewAtom,
  removeWellAtom,
  restoreWellAtom,
  wellDetailUrl,
  wellOverviewDataAtom,
  wellOverviewErrorAtom,
  wellOverviewUrl,
} from './atoms/wells';
import { selectedWellIndexAtom } from './atoms/layout';
import { AddWellPanel } from './add-well-panel';
import { WellEditPanel } from './well-edit-panel';

// RC Well Overview — a thin doctype wrapper over the shared section
// (pages/documents/structuring/wells-overview-section). All behavior lives in the
// shared component; this file supplies the RC wiring:
//   • the RC wells atom bundle + URL builders (remove/restore built by id);
//   • the document-scoped `selectedWellIndexAtom(documentId)` drill-in atom;
//   • the RC staged shape (operator / property…) and none of the AFE-only rows;
//   • no extra sibling reload — unlike DO (whose interest lines mute on a well's
//     removal), RC has no section coupled to a well's lifecycle: line items are
//     Document-anchored and grouped by extracted well name, not by targets;
//   • a destructive-delete confirm modal (the app norm for a destructive action).

const RC_WELLS_OVERVIEW_CONFIG: WellsOverviewConfig<StagedWell> = {
  wellOverviewDataAtom,
  wellOverviewErrorAtom,
  fetchWellOverviewAtom,
  removeWellAtom,
  restoreWellAtom,
  wellOverviewUrl,
  wellDetailUrl,
  // RC's drill-in selection is document-scoped (atomFamily keyed on documentId).
  getSelectedWellIndexAtom: (documentId) => selectedWellIndexAtom(documentId),
  // RC staged shape only — no AFE-only rows and no DO division-order number.
  // Legal Description spans the full cell width.
  stagedOverviewFields: (staged) => [
    { label: 'UWI / API Number', value: staged.uwiApi ?? '—' },
    { label: 'County/State', value: staged.countyState ?? '—' },
    { label: 'Operator', value: staged.operator ?? '—' },
    ...(staged.propertyName
      ? [{ label: 'Property Name', value: staged.propertyName }]
      : []),
    ...(staged.propertyNumber
      ? [{ label: 'Property Number', value: staged.propertyNumber }]
      : []),
    ...(staged.legalDescription
      ? [
          {
            label: 'Legal Description',
            value: staged.legalDescription,
            fullWidth: true,
          },
        ]
      : []),
  ],
  // RC has no sibling section coupled to a well's removal/restore, so there is
  // nothing extra to refetch after the mutation.
  useExtraReload: () => () => {},
  // Delete is destructive → confirm first (soft-delete makes it recoverable, but
  // confirmation is the app norm).
  confirmRemoval: {
    heading: 'Delete Well',
    primaryButtonText: 'Delete',
    message: (wellName) =>
      `Are you sure you want to delete ${wellName}? You can undo this from the removed well’s Undo action.`,
  },
  AddWellPanel,
  WellEditPanel,
};

interface Props {
  documentId: number | string;
}

export const WellsOverviewSection: FC<Props> = ({ documentId }) => (
  <SharedWellsOverviewSection<StagedWell>
    documentId={documentId}
    config={RC_WELLS_OVERVIEW_CONFIG}
  />
);
